import type {
  CommendaOSFilter,
  ContextBinding,
  DocumentProps,
  FormComponent,
  FormDSL,
} from '@commenda/types';
import { COMMENDA_OS_DATA_SOURCE } from '../../dsl-constants';
import { DocumentEffectivePeriodHelper } from '../document-effective-period-helper';
import { walkComponents } from '../dsl-traversal';

/**
 * Generic Commenda OS field mapping produced for any component whose
 * `dataSource.commendaOS` describes a read/write path on a backend entity, OR
 * one of the special component types (GovernmentIdentifier, Location,
 * Document) whose paths are derived from `props` instead.
 */
export interface CommendaOSFieldMapping {
  fieldId: string;
  /** Entity for read operations */
  entity: string;
  /**
   * Absent on a write-only field, which is submitted but never prefilled.
   */
  readPath?: string;
  createPath?: string;
  updatePath?: string;
  /** Write configuration for upsert operations (used for relation entities like governmentIdentifier) */
  write?: {
    /** Entity for write operations (if different from read entity, e.g., GovernmentIdentifier reads from corporation but writes to governmentIdentifier) */
    entity: string;
    /** Write path for upsert operations */
    path: string;
  };
  mode: 'read' | 'read-write' | undefined;
  /** Context bindings - allows this field to use values from other fields as context */
  contextBindings?: ContextBinding[];
  /** Filters for relation entities (e.g., governmentIdentifiers[identifierTypeId=GID:US:EIN]) */
  filters?: CommendaOSFilter[];
}

/**
 * GovernmentIdentifier uses props.identifierTypeId + props.mode instead of the
 * standard dataSource.commendaOS.field structure.
 * - Read entity is 'corporation' (corporation.governmentIdentifiers[identifierTypeId=X].value).
 * - Write entity is 'governmentIdentifier' (upserts with filters).
 */
function extractGovernmentIdentifierMapping(
  component: FormComponent
): CommendaOSFieldMapping | null {
  const props = component.props as
    | { identifierTypeId?: string; mode?: 'read' | 'read-write' }
    | undefined;
  const { dataSource } = component;

  if (!component.fieldId || !props?.identifierTypeId) {
    return null;
  }

  const { identifierTypeId } = props;
  const mode = props.mode || 'read-write';
  const contextBindings = dataSource?.commendaOS?.contextBindings;

  return {
    fieldId: component.fieldId,
    entity: 'corporation',
    readPath: `governmentIdentifiers[identifierTypeId=${identifierTypeId}].value`,
    write: { entity: 'governmentIdentifier', path: 'value' },
    mode,
    contextBindings,
    filters: [{ key: 'identifierTypeId', value: identifierTypeId }],
  };
}

/**
 * Location uses props.locationType + props.mode instead of the standard
 * dataSource.commendaOS.field structure.
 * - Read entity is 'corporation' (corporation.locations[locationType=X]).
 * - Write entity is 'location' (upserts with filters).
 * - Value is an object with { address, name?, startDate? }.
 */
function extractLocationMapping(
  component: FormComponent
): CommendaOSFieldMapping | null {
  const props = component.props as
    | {
        locationType?: string;
        mode?: 'read' | 'read-write';
        showName?: boolean;
        showStartDate?: boolean;
        entity?: 'corporation' | 'keyPerson';
      }
    | undefined;
  const { dataSource } = component;

  if (!component.fieldId || !props?.locationType) {
    return null;
  }

  const { locationType } = props;
  const mode = props.mode || 'read-write';
  const contextBindings = dataSource?.commendaOS?.contextBindings;
  const locationEntity = props.entity || 'corporation';

  // Key person location: reads from keyPerson.locations (array filtered on frontend),
  // writes to keyPerson entity via locations field update
  if (locationEntity === 'keyPerson') {
    return {
      fieldId: component.fieldId,
      entity: 'keyPerson',
      readPath: 'locations',
      // Special marker so submission logic knows to format as locations array
      write: { entity: 'keyPerson', path: 'keyPersonLocation' },
      mode,
      contextBindings,
    };
  }

  return {
    fieldId: component.fieldId,
    entity: 'corporation',
    readPath: `locations[locationType=${locationType}]`,
    write: { entity: 'location', path: 'location' },
    mode,
    contextBindings,
    filters: [{ key: 'locationType', value: locationType }],
  };
}

/**
 * Document uses props.documentSubType + props.mode instead of the standard
 * dataSource.commendaOS.field structure.
 * - Read entity is 'corporation' (corporation.documents[documentSubType=X]).
 * - Write entity is 'document' (upserts via document entity with subType filter).
 * - props.effectiveStartDate / props.effectiveEndDate, when set, are added to
 *   the filters so a time-sensitive document is read and saved for that period.
 * - Form value is an object: { fileUrl, fileName, effectiveStartDate?, effectiveEndDate? }.
 */
function extractDocumentMapping(
  component: FormComponent
): CommendaOSFieldMapping | null {
  const props = component.props as
    | (Partial<DocumentProps> & { documentSubType?: string | null })
    | undefined;
  const { dataSource } = component;

  if (!component.fieldId || !props?.documentSubType) {
    return null;
  }

  const { documentSubType } = props;
  const mode = props.mode || 'read-write';
  const contextBindings = dataSource?.commendaOS?.contextBindings;
  const filters = DocumentEffectivePeriodHelper.getDocumentFilters(
    documentSubType,
    props
  );

  return {
    fieldId: component.fieldId,
    entity: 'corporation',
    readPath: DocumentEffectivePeriodHelper.buildDocumentsReadPath(filters),
    write: { entity: 'document', path: 'document' },
    mode,
    contextBindings,
    filters,
  };
}

/**
 * Extract a Commenda OS field mapping from a single component. Dispatches to
 * type-specific extractors for components with non-standard prop shapes
 * (GovernmentIdentifier, Location, Document). Returns null for components
 * handled by other extractors (DirectorDocument, RoleAssignment,
 * EntitySelectOrCreate) and for components without commenda-os data source.
 */
function extractFieldMapping(
  component: FormComponent
): CommendaOSFieldMapping | null {
  if (component.component === 'GovernmentIdentifier') {
    return extractGovernmentIdentifierMapping(component);
  }
  if (component.component === 'Location') {
    return extractLocationMapping(component);
  }
  if (component.component === 'Document') {
    return extractDocumentMapping(component);
  }
  // DirectorDocument is handled via Person API, not DSL
  if (component.component === 'DirectorDocument') {
    return null;
  }
  // RoleAssignment handled separately (role + key person + nested form)
  if (component.component === 'RoleAssignment') {
    return null;
  }
  // EntitySelectOrCreate handled separately
  if (component.component === 'EntitySelectOrCreate') {
    return null;
  }

  const { dataSource } = component;
  if (
    !dataSource ||
    dataSource.type !== COMMENDA_OS_DATA_SOURCE ||
    !dataSource.commendaOS
  ) {
    return null;
  }

  const {
    entity,
    field,
    mode: dsMode,
    contextBindings,
    filters,
  } = dataSource.commendaOS;

  // RegisteredAgent historically stored mode under props instead of
  // dataSource.commendaOS.mode. Fall back to props.mode so existing saved
  // forms continue to write correctly.
  const mode =
    dsMode ??
    (component.component === 'RegisteredAgent'
      ? (component.props?.mode as 'read' | 'read-write' | undefined)
      : undefined);

  // Any path earns a mapping, not `read` specifically: requiring it dropped every write-only
  // binding silently, so those fields were never sent.
  if (
    !component.fieldId ||
    !entity ||
    !(field?.read || field?.create || field?.update || field?.write)
  ) {
    return null;
  }

  const fieldConfig = field as {
    read?: string;
    create?: string;
    update?: string;
    write?: string;
  };

  return {
    fieldId: component.fieldId,
    entity,
    readPath: fieldConfig.read,
    createPath: fieldConfig.create,
    updatePath: fieldConfig.update,
    write: fieldConfig.write ? { entity, path: fieldConfig.write } : undefined,
    mode,
    contextBindings,
    filters,
  };
}

/**
 * Extract all Commenda OS field mappings from the form DSL.
 *
 * Walks every top-level component and ALSO iterates one level into
 * `component.template` (e.g. for components inside repeatable modules). Does
 * NOT recurse deeper — some stored form templates rely on this exact depth.
 */
export function extractCommendaOSFields(
  dsl: FormDSL
): CommendaOSFieldMapping[] {
  const mappings: CommendaOSFieldMapping[] = [];
  walkComponents(dsl, (component) => {
    const mapping = extractFieldMapping(component);
    if (mapping) {
      mappings.push(mapping);
    }

    // Handle nested components (e.g., in repeatable modules) — one level only
    if (component.template) {
      component.template.forEach((nestedComponent) => {
        const nestedMapping = extractFieldMapping(nestedComponent);
        if (nestedMapping) {
          mappings.push(nestedMapping);
        }
      });
    }
  });
  return mappings;
}
